import React, { useEffect, useRef } from 'react';
import { run as CookieConsentRun, acceptedService, setLanguage } from 'vanilla-cookieconsent';
import { useTranslation } from 'react-i18next';

import "vanilla-cookieconsent/dist/cookieconsent.css";

const CookieConsentBanner = () => {
  const { i18n } = useTranslation();
  const instanceRef = useRef(null);

  useEffect(() => {
    // Define categories and services constants
    const CAT_NECESSARY = "necessary";
    const CAT_ANALYTICS = "analytics";
    const CAT_ADVERTISEMENT = "advertisement";
    const CAT_FUNCTIONALITY = "functionality";
    const CAT_SECURITY = "security";

    const SERVICE_AD_STORAGE = 'ad_storage';
    const SERVICE_AD_USER_DATA = 'ad_user_data';
    const SERVICE_AD_PERSONALIZATION = 'ad_personalization';
    const SERVICE_ANALYTICS_STORAGE = 'analytics_storage';
    const SERVICE_FUNCTIONALITY_STORAGE = 'functionality_storage';
    const SERVICE_PERSONALIZATION_STORAGE = 'personalization_storage';
    const SERVICE_SECURITY_STORAGE = 'security_storage';

    // Define dataLayer and the gtag function
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}

    // Set default consent to 'denied' (this should happen before changing any other dataLayer)
    gtag('consent', 'default', {
        [SERVICE_AD_STORAGE]: 'denied',
        [SERVICE_AD_USER_DATA]: 'denied',
        [SERVICE_AD_PERSONALIZATION]: 'denied',
        [SERVICE_ANALYTICS_STORAGE]: 'denied',
        [SERVICE_FUNCTIONALITY_STORAGE]: 'denied',
        [SERVICE_PERSONALIZATION_STORAGE]: 'denied',
        [SERVICE_SECURITY_STORAGE]: 'denied',
    });

    /** 
     * Update gtag consent according to the users choices made in CookieConsent UI
     */
    function updateGtagConsent() {
        gtag('consent', 'update', {
            [SERVICE_ANALYTICS_STORAGE]: acceptedService(SERVICE_ANALYTICS_STORAGE, CAT_ANALYTICS) ? 'granted' : 'denied',
            [SERVICE_AD_STORAGE]: acceptedService(SERVICE_AD_STORAGE, CAT_ADVERTISEMENT) ? 'granted' : 'denied',
            [SERVICE_AD_USER_DATA]: acceptedService(SERVICE_AD_USER_DATA, CAT_ADVERTISEMENT) ? 'granted' : 'denied',
            [SERVICE_AD_PERSONALIZATION]: acceptedService(SERVICE_AD_PERSONALIZATION, CAT_ADVERTISEMENT) ? 'granted' : 'denied',
            [SERVICE_FUNCTIONALITY_STORAGE]: acceptedService(SERVICE_FUNCTIONALITY_STORAGE, CAT_FUNCTIONALITY) ? 'granted' : 'denied',
            [SERVICE_PERSONALIZATION_STORAGE]: acceptedService(SERVICE_PERSONALIZATION_STORAGE, CAT_FUNCTIONALITY) ? 'granted' : 'denied',
            [SERVICE_SECURITY_STORAGE]: acceptedService(SERVICE_SECURITY_STORAGE, CAT_SECURITY) ? 'granted' : 'denied',
        });
    }

    // Function to check consent status (exposed globally for GTM)
    const checkConsentStatus = () => {
      const match = document.cookie.match('(^|;)\\s*cc_cookie\\s*=\\s*([^;]+)');
      if (!match) return 'denied';
      try {
        const cookie = decodeURIComponent(match[2]);
        const consent = JSON.parse(cookie);
        return (consent.categories && consent.categories.indexOf('analytics') > -1) ? 'granted' : 'denied';
      } catch(e) {
        return 'denied';
      }
    };

    // Expose functions globally for GTM
    window.checkConsentStatus = checkConsentStatus;
    window.showCookiePreferences = () => {
      // This will be set after CookieConsentRun is called
      if (instanceRef.current && typeof instanceRef.current.showPreferences === 'function') {
        instanceRef.current.showPreferences();
      }
    };

    // Initialize cookie consent
    const runCookieConsent = () => {
      if (!document.body) {
        return setTimeout(runCookieConsent, 50);
      }

      instanceRef.current = CookieConsentRun({
        cookie: {
          name: 'cc_cookie'
        },
        guiOptions: {
          consentModal: {
            layout: 'cloud inline',
            position: 'bottom left',
            equalWeightButtons: true,
            flipButtons: false
          },
          preferencesModal: {
            layout: 'box',
            equalWeightButtons: true,
            flipButtons: false
          }
        },
        onFirstConsent: function (e) {
          updateGtagConsent();
        },
        onConsent: function (e) {
          updateGtagConsent();
          if (window.dataLayer) {
            window.dataLayer.push({ event: 'cookie_consent_updated' });
          }
        },
        onChange: function (e) {
          updateGtagConsent();
        },
       
        categories: {
          [CAT_NECESSARY]: {
            enabled: true,  // this category is enabled by default
            readOnly: true,  // this category cannot be disabled
          },
          [CAT_ANALYTICS]: {
            autoClear: {
              cookies: [
                {
                  name: /^_ga/,   // regex: match all cookies starting with '_ga'
                },
                {
                  name: '_gid',   // string: exact cookie name
                }
              ]
            },
            services: {
              [SERVICE_ANALYTICS_STORAGE]: {
                label: 'Enables storage (such as cookies) related to analytics e.g. visit duration.',
              }
            }
          },
          [CAT_ADVERTISEMENT]: {
            services: {
              [SERVICE_AD_STORAGE]: {
                label: 'Enables storage (such as cookies) related to advertising.',
              },
              [SERVICE_AD_USER_DATA]: {
                label: 'Sets consent for sending user data related to advertising to Google.',
              },
              [SERVICE_AD_PERSONALIZATION]: {
                label: 'Sets consent for personalized advertising.',
              },
            }
          },
          [CAT_FUNCTIONALITY]: {
            services: {
              [SERVICE_FUNCTIONALITY_STORAGE]: {
                label: 'Enables storage that supports the functionality of the website or app e.g. language settings.',
              },
              [SERVICE_PERSONALIZATION_STORAGE]: {
                label: 'Enables storage related to personalization e.g. video recommendations.',
              },
            }
          },
          [CAT_SECURITY]: {
            services: {
              [SERVICE_SECURITY_STORAGE]: {
                label: 'Enables storage related to security such as authentication functionality, fraud prevention, and other user protection.',
              },
            }
          }
        },
        language: {
          default: i18n.language || 'no',
          autoDetect: 'browser',
          translations: {
            en: {
              consentModal: {
                title: 'We use cookies',
                description: 'This website uses essential cookies to ensure its proper operation and tracking cookies to understand how you interact with it. The latter will be set only after consent.',
                acceptAllBtn: 'Accept all',
                acceptNecessaryBtn: 'Reject all',
                showPreferencesBtn: 'Manage Individual preferences'
              },
              preferencesModal: {
                title: 'Manage cookie preferences',
                acceptAllBtn: 'Accept all',
                acceptNecessaryBtn: 'Reject all',
                savePreferencesBtn: 'Accept current selection',
                closeIconLabel: 'Close modal',
                sections: [
                  {
                    title: "Cookie usage",
                    description: "We use cookies to ensure the basic functionalities of the website and to enhance your online experience."
                  },
                  {
                    title: "Strictly necessary cookies",
                    description: "These cookies are essential for the proper functioning of the website, for example for user authentication.",
                    linkedCategory: CAT_NECESSARY,
                  },
                  {
                    title: "Analytics",
                    description: 'Cookies used for analytics help collect data that allows services to understand how users interact with a particular service. These insights allow services both to improve content and to build better features that improve the user\'s experience.',
                    linkedCategory: CAT_ANALYTICS,
                    cookieTable: {
                      headers: {
                        name: "Name",
                        domain: "Service",
                        description: "Description",
                        expiration: "Expiration"
                      },
                      body: [
                        {
                          name: "_ga",
                          domain: "Google Analytics",
                          description: "Cookie set by <a href=\"https://business.safety.google/adscookies/\">Google Analytics</a>",
                          expiration: "Expires after 12 days"
                        },
                        {
                          name: "_gid",
                          domain: "Google Analytics",
                          description: "Cookie set by <a href=\"https://business.safety.google/adscookies/\">Google Analytics</a>",
                          expiration: "Session"
                        }
                      ]
                    }
                  },
                  {
                    title: 'Advertising',
                    description: 'Google uses cookies for advertising, including serving and rendering ads, personalizing ads (depending on your ad settings at <a href=\"https://g.co/adsettings\">g.co/adsettings</a>), limiting the number of times an ad is shown to a user, muting ads you have chosen to stop seeing, and measuring the effectiveness of ads.',
                    linkedCategory: CAT_ADVERTISEMENT,
                  },
                  {
                    title: 'Functionality',
                    description: 'Cookies used for functionality allow users to interact with a service or site to access features that are fundamental to that service. Things considered fundamental to the service include preferences like the user\'s choice of language, product optimizations that help maintain and improve a service, and maintaining information relating to a user\'s session, such as the content of a shopping cart.',
                    linkedCategory: CAT_FUNCTIONALITY,
                  },
                  {
                    title: 'Security',
                    description: 'Cookies used for security authenticate users, prevent fraud, and protect users as they interact with a service.',
                    linkedCategory: CAT_SECURITY,
                  },
                  {
                    title: 'More information',
                    description: 'For any queries in relation to the policy on cookies and your choices, please <a href="/skjema/kontakt-oss">contact us</a>.'
                  }
                ]
              }
            },
            no: {
              consentModal: {
                title: 'Vi bruker cookies',
                description: 'Dette nettstedet bruker nødvendige cookies for å sikre riktig drift og sporingscookies for å forstå hvordan du samhandler med det. De sistnevnte vil kun bli satt etter samtykke.',
                acceptAllBtn: 'Godta alle',
                acceptNecessaryBtn: 'Avvis alle',
                showPreferencesBtn: 'Administrer individuelle preferanser'
              },
              preferencesModal: {
                title: 'Administrer cookie-preferanser',
                acceptAllBtn: 'Godta alle',
                acceptNecessaryBtn: 'Avvis alle',
                savePreferencesBtn: 'Godta gjeldende valg',
                closeIconLabel: 'Lukk modal',
                sections: [
                  {
                    title: "Cookie-bruk",
                    description: "Vi bruker cookies for å sikre nettstedets grunnleggende funksjoner og for å forbedre din online opplevelse."
                  },
                  {
                    title: "Strengt nødvendige cookies",
                    description: "Disse cookies er nødvendige for nettstedets riktige funksjon, for eksempel for brukerautentisering.",
                    linkedCategory: CAT_NECESSARY,
                  },
                  {
                    title: "Analyse",
                    description: 'Cookies brukt til analyse hjelper med å samle inn data som lar tjenester forstå hvordan brukere samhandler med en bestemt tjeneste. Disse innsiktene lar tjenester både forbedre innhold og bygge bedre funksjoner som forbedrer brukeropplevelsen.',
                    linkedCategory: CAT_ANALYTICS,
                    cookieTable: {
                      headers: {
                        name: "Navn",
                        domain: "Tjeneste",
                        description: "Beskrivelse",
                        expiration: "Utløp"
                      },
                      body: [
                        {
                          name: "_ga",
                          domain: "Google Analytics",
                          description: "Cookie satt av <a href=\"https://business.safety.google/adscookies/\">Google Analytics</a>",
                          expiration: "Utløper etter 12 dager"
                        },
                        {
                          name: "_gid",
                          domain: "Google Analytics",
                          description: "Cookie satt av <a href=\"https://business.safety.google/adscookies/\">Google Analytics</a>",
                          expiration: "Økt"
                        }
                      ]
                    }
                  },
                  {
                    title: 'Reklame',
                    description: 'Google bruker cookies for reklame, inkludert levering og gjengivelse av annonser, personlig tilpassing av annonser (avhengig av dine annonseinnstillinger på <a href=\"https://g.co/adsettings\">g.co/adsettings</a>), begrensning av antall ganger en annonse vises til en bruker, demping av annonser du har valgt å ikke se lenger, og måling av effektiviteten til annonser.',
                    linkedCategory: CAT_ADVERTISEMENT,
                  },
                  {
                    title: 'Funksjonalitet',
                    description: 'Cookies brukt for funksjonalitet lar brukere samhandle med en tjeneste eller nettsted for å få tilgang til funksjoner som er grunnleggende for den tjenesten. Ting som anses som grunnleggende for tjenesten inkluderer preferanser som brukerens språkvalg, produktoptimaliseringer som hjelper med å vedlikeholde og forbedre en tjeneste, og vedlikeholde informasjon relatert til en brukers økt, som innholdet i en handlekurv.',
                    linkedCategory: CAT_FUNCTIONALITY,
                  },
                  {
                    title: 'Sikkerhet',
                    description: 'Cookies brukt for sikkerhet autentiserer brukere, forhindrer svindel og beskytter brukere mens de samhandler med en tjeneste.',
                    linkedCategory: CAT_SECURITY,
                  },
                  {
                    title: 'Mer informasjon',
                    description: 'For spørsmål om retningslinjene for cookies og dine valg, vennligst <a href="/skjema/kontakt-oss">kontakt oss</a>.'
                  }
                ]
              }
            }
          }
        }
      });
    };

    // Start the initialization
    runCookieConsent();

    // Cleanup function
    return () => {
      // The library handles its own cleanup
    };
  }, []); // Only run once on mount

  // Separate effect to handle language changes
  useEffect(() => {
    if (instanceRef.current && i18n.language) {
      // Update the language of the cookie consent banner
      setLanguage(i18n.language);
    }
  }, [i18n.language]);

  // This component doesn't render anything visible
  // The cookie consent banner is injected into the DOM by the library
  return null;
};

export default CookieConsentBanner; 